import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface CreatedUser {
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
}

interface MiniUserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  initialName?: string;
  onSaved: (userId: string) => void;
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  let result = '';
  for (let i = 0; i < 16; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export function MiniUserFormDialog({
  open,
  onOpenChange,
  customerId,
  initialName,
  onSaved,
}: MiniUserFormDialogProps) {
  const { t } = useTranslation('quotes');

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Parse initial name into first/last
      if (initialName) {
        const parts = initialName.trim().split(/\s+/);
        setFirstname(parts[0] || '');
        setLastname(parts.slice(1).join(' ') || '');
      } else {
        setFirstname('');
        setLastname('');
      }
      setEmail('');
      setPassword(generatePassword());
      setPosition('');
      setPhone('');
    }
  }, [open, initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstname.trim() || !lastname.trim() || !email.trim() || !password) return;

    setSaving(true);
    try {
      const response = await api.post<{ user: CreatedUser }>(
        `/v4/legacy/users?organisation=${customerId}`,
        {
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          email: email.trim(),
          password,
          position: position.trim() || undefined,
          phone: phone.trim() || undefined,
          tags: ['Contractual Manager'],
        }
      );
      const saved = response.user;
      onSaved(saved._id);
      toast.success(t('miniForm.createUser'));
      onOpenChange(false);
    } catch {
      toast.error(t('miniForm.createUser'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('miniForm.createUser')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('miniForm.createUser')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('miniForm.firstName')} *</Label>
              <Input value={firstname} onChange={(e) => setFirstname(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>{t('miniForm.lastName')} *</Label>
              <Input value={lastname} onChange={(e) => setLastname(e.target.value)} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('miniForm.email')} *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1.5">
            <Label>{t('miniForm.password')} *</Label>
            <div className="flex gap-2">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPassword(generatePassword())}
                title={t('miniForm.generatePassword')}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('miniForm.position')}</Label>
              <Input value={position} onChange={(e) => setPosition(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('miniForm.phone')}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('form.cancel')}
            </Button>
            <Button type="submit" disabled={saving || !firstname.trim() || !lastname.trim() || !email.trim() || !password}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {saving ? t('miniForm.saving') : t('miniForm.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
